import React, { SyntheticEvent, useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';

import { Dropdown, Toggle } from '@trqk/components';
import { locale } from '@trqk/utils';
import { useGetTrqkUserDetectionNotificationQuery } from '@trqk/api';

import { periodOptions } from './constants';

interface DetectionNotificationsProps {
    userId: string;
}

const UPDATE_TRQK_USER_DETECTION_NOTIFICATION_SETTINGS = gql`
    mutation updateTrqkUserDetectionNotificationSettings(
        $enabled: Boolean!
        $period: Int!
    ) {
        updateTrqkUserDetectionNotificationSettings(
            enabled: $enabled
            period: $period
        ) {
            id
            detection_notification_enabled
            detection_notification_period
        }
    }
`;

const defaultPeriodIndex = 1;
const defaultPeriodValue = periodOptions[defaultPeriodIndex].value.period;

export const DetectionNotifications = ({
    userId,
}: DetectionNotificationsProps): JSX.Element => {
    const trqkUserQuery = useGetTrqkUserDetectionNotificationQuery({
        variables: { id: userId },
        returnPartialData: true,
    });
    const trqkUser = trqkUserQuery?.data?.getTrqkUser ?? null;
    const detection_notification_enabled =
        trqkUser?.detection_notification_enabled ?? false;
    const detection_notification_period =
        trqkUser?.detection_notification_period ?? defaultPeriodValue;
    const [updateTrqkUserDetectionNotificationSettings] = useMutation<{
        updateTrqkUserDetectionNotificationSettings: TrqkUser;
    }>(UPDATE_TRQK_USER_DETECTION_NOTIFICATION_SETTINGS);
    const defaultOptionIndex = periodOptions.findIndex(
        (option: DropdownOption<{ period: number }>): boolean =>
            option.value.period === detection_notification_period,
    );

    const [selectedIndex, setSelectedIndex] = useState(defaultOptionIndex);

    const periodOfSelectedIndex = periodOptions[selectedIndex].value.period;

    useEffect(() => {
        // The trqkUser.detection_notification_period takes a moment to be fetched from the API.
        // When it becomes available, this ensures that the dropdown is updated to reflect the
        // currently selected period.
        if (
            periodOfSelectedIndex !== null &&
            detection_notification_period !== null &&
            periodOfSelectedIndex !== detection_notification_period
        ) {
            const indexOfCurrentPeriod = periodOptions.findIndex(
                (option: DropdownOption<{ period: number }>): boolean =>
                    option.value.period === detection_notification_period,
            );

            if (indexOfCurrentPeriod > -1) {
                setSelectedIndex(indexOfCurrentPeriod);
            }
        }
    }, [detection_notification_period, periodOfSelectedIndex]);

    return (
        <div className="border border-tertiary-200 rounded-lg p-6 space-y-4">
            <h3 className="text-lg">
                {locale.enUS.ACCOUNT.SECTIONS.NOTIFICATIONS.DETECTIONS.TITLE}
            </h3>
            <p>
                {
                    locale.enUS.ACCOUNT.SECTIONS.NOTIFICATIONS.DETECTIONS
                        .DESCRIPTION
                }
            </p>
            <div className="flex gap-x-4 items-center">
                <Toggle
                    checked={detection_notification_enabled}
                    onChange={(event: SyntheticEvent) => {
                        const target = event.target as HTMLInputElement;

                        updateTrqkUserDetectionNotificationSettings({
                            variables: {
                                enabled: target.checked,
                                period: detection_notification_period,
                            },
                        });
                    }}
                    label={
                        locale.enUS.ACCOUNT.SECTIONS.NOTIFICATIONS.DETECTIONS
                            .ENABLE
                    }
                    inputId="enable-detection-notifications"
                    size="large"
                    color="primary"
                    width={130}
                />
                <Dropdown<{ period: number }>
                    defaultIndex={
                        defaultOptionIndex > -1
                            ? defaultOptionIndex
                            : defaultPeriodIndex
                    }
                    description={
                        locale.enUS.ACCOUNT.SECTIONS.NOTIFICATIONS.DETECTIONS
                            .PERIOD_DESCRIPTION
                    }
                    externalState={{
                        selectedIndex,
                        setSelectedIndex,
                    }}
                    options={periodOptions}
                    handleChange={(value: { period: number }): void => {
                        updateTrqkUserDetectionNotificationSettings({
                            variables: {
                                enabled: detection_notification_enabled,
                                period: value.period,
                            },
                        });
                    }}
                />
            </div>
        </div>
    );
};
